import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TripCard = ({ trip, onModify, onCancel }) => {
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    const colors = {
      upcoming: 'bg-primary/10 text-primary border-primary/20',
      completed: 'bg-success/10 text-success border-success/20',
      cancelled: 'bg-destructive/10 text-destructive border-destructive/20',
      ongoing: 'bg-warning/10 text-warning border-warning/20'
    };
    return colors?.[status] || colors?.upcoming;
  };

  const getStatusIcon = (status) => {
    const icons = {
      upcoming: 'Clock',
      completed: 'CheckCircle2',
      cancelled: 'XCircle',
      ongoing: 'Navigation'
    };
    return icons?.[status] || 'Clock';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const calculateCountdown = (date, time) => {
    const tripDateTime = new Date(`${date}T${time}`);
    const now = new Date();
    const diff = tripDateTime - now;

    if (diff <= 0) return null;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ${hours}h`;
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  const countdown = trip?.status === 'upcoming' ? calculateCountdown(trip?.date, trip?.time) : null;

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6 hover:shadow-lg transition-all duration-300">
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
        <div className="w-full lg:w-48 h-48 lg:h-auto rounded-lg overflow-hidden flex-shrink-0">
          <Image
            src={trip?.vehicleImage}
            alt={trip?.vehicleImageAlt}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="flex-1 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                  {trip?.vehicleType}
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(trip?.status)}`}>
                  <Icon name={getStatusIcon(trip?.status)} size={12} className="inline mr-1" />
                  {trip?.status?.charAt(0)?.toUpperCase() + trip?.status?.slice(1)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Booking ID: <span className="font-medium text-foreground">{trip?.bookingId}</span>
              </p>
            </div>

            {countdown && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg px-4 py-2 text-center">
                <p className="text-xs text-muted-foreground">Starts in</p>
                <p className="text-lg font-bold text-primary whitespace-nowrap">{countdown}</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="MapPin" size={16} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">From</p>
                  <p className="text-sm font-medium text-foreground truncate">{trip?.from}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="MapPin" size={16} className="text-secondary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">To</p>
                  <p className="text-sm font-medium text-foreground truncate">{trip?.to}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Calendar" size={16} className="text-accent-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="text-sm font-medium text-foreground">{formatDate(trip?.date)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Clock" size={16} className="text-warning" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Time</p>
                  <p className="text-sm font-medium text-foreground">{formatTime(trip?.time)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div>
              <p className="text-xs text-muted-foreground">Total Fare</p>
              <p className="text-xl md:text-2xl font-bold text-foreground whitespace-nowrap">
                ₹{trip?.fare?.toLocaleString('en-IN')}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 justify-end">
              {trip?.status === 'upcoming' && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    iconPosition="left"
                    onClick={() => onModify(trip)}
                  >
                    Modify
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    iconName="XCircle"
                    iconPosition="left"
                    onClick={() => onCancel(trip)}
                  >
                    Cancel
                  </Button>
                </>
              )}
              {trip?.status === 'completed' && (
                <Button
                  variant="default"
                  size="sm"
                  iconName="Star"
                  iconPosition="left"
                  onClick={() => navigate('/contact-support')}
                >
                  Rate Trip
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                iconPosition="left"
                onClick={() => navigate('/booking-confirmation')}
              >
                View Details
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripCard;